import { hostname } from "@/components/molecules/website-proposal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/shadcn-io/spinner";
import {
  useEnrichmentState,
  useStartEnrichment,
} from "@/features/enrichment/hooks/use-enrichment";
import type { EnrichmentScope } from "@/features/enrichment/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  ArrowsClockwiseIcon,
  EyeIcon,
  SparkleIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * "Fast track" — the shortcut at the top of every Add dialog.
 *
 * The client already told us their website during onboarding, and that website
 * already lists their prestations and answers their customers' recurring
 * questions. Asking them to retype all of it into a form is the thing that
 * stalls a setup, so the form leads with the alternative: read the site.
 *
 * It only *starts* the reading. What the site suggests is a proposal, reviewed
 * on the page itself (`FastTrackReview`, above the list) where there is room
 * for a list of twenty entries — so starting closes the dialog (`onLeave`), and
 * while a reading is going or a proposal is waiting, the button leads there
 * instead of offering a second one.
 *
 * It is a *complement* to the form underneath, never a replacement — the client
 * can ignore it entirely and type their entry by hand, which is why this renders
 * as a strip above the fields rather than as a screen of its own.
 *
 * It only appears when there is a site to read (no site, no promise), and when
 * the server says this scope is still on offer: a tenant whose catalogue already
 * arrives over an integration's API does not want a second, blurrier source.
 */
export const FastTrackPanel = ({
  scope,
  onLeave,
}: {
  scope: EnrichmentScope;
  /** Close the dialog: the reading and its review happen on the page. */
  onLeave?: () => void;
}) => {
  const { t } = useTranslation("fastTrack");
  const { data } = useEnrichmentState();
  const startRun = useStartEnrichment();

  const website = data?.website || "";
  const run = data?.runs?.[scope] || null;
  const isRunning = run?.status === "running" || startRun.isPending;
  const isWaiting = run?.status === "done" && run.review?.[scope] === "pending";

  // Nothing to read means nothing to offer. The client sets their website from
  // the company page; a dead button explaining that would be worse than silence.
  if (!website) return null;

  // Same silence when the server no longer offers this scope for this tenant —
  // unless a reading of it is still going or waiting, which the page shows.
  if (!data?.scopes?.includes(scope) && !isRunning && !isWaiting) return null;

  const handleStart = async () => {
    try {
      await startRun.mutateAsync({ scopes: [scope] });
      onLeave?.();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("toast.startError"),
      );
    }
  };

  return (
    <div className="bg-muted/40 flex flex-col gap-3 rounded-lg border border-dashed p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-1">
        <p className="flex items-center gap-2 text-sm font-medium">
          <SparkleIcon className="h-4 w-4" weight="duotone" />
          {t(`scopes.${scope}.title`)}
        </p>
        <p className="text-muted-foreground text-sm">
          {isRunning
            ? t("dialog.running", { website: hostname(run?.url || website) })
            : isWaiting
              ? t("dialog.waiting", { website: hostname(run?.url || website) })
              : t(`scopes.${scope}.description`, {
                  website: hostname(website),
                })}
        </p>
      </div>

      {isRunning || isWaiting ? (
        <Button
          type="button"
          variant="outline"
          onClick={onLeave}
          className="shrink-0"
        >
          {isRunning ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <EyeIcon className="h-4 w-4" />
          )}
          {t("actions.see")}
        </Button>
      ) : (
        <Button type="button" onClick={handleStart} className="shrink-0">
          {run ? (
            <>
              <ArrowsClockwiseIcon className="h-4 w-4" weight="bold" />
              {t("actions.again")}
            </>
          ) : (
            <>
              <SparkleIcon className="h-4 w-4" weight="fill" />
              {t("actions.start")}
            </>
          )}
        </Button>
      )}
    </div>
  );
};
